// ============================================================
//  src/ui.js — Utilidades de interfaz de usuario
//  Sistema de toasts, cambio de vistas y helpers del DOM.
// ============================================================

/**
 * Escapa caracteres HTML peligrosos para evitar inyección/XSS.
 * @param {any} str
 * @returns {string}
 */
export function escapeHTML(str) {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

// ============================================================
//  Sistema de Toasts (notificaciones flotantes)
// ============================================================

let contadorToast = 0;

/**
 * Muestra una notificación toast flotante.
 * Se apila si hay múltiples llamadas. Se autodestruye.
 * @param {string} texto - Mensaje a mostrar
 * @param {'exito'|'ok'|'error'|'info'|'cargando'} tipo - Variante visual ('ok' es alias visual de 'exito')
 * @param {number} duracion - Milisegundos hasta autocierre (0 = manual)
 */
export function mostrarToast(texto, tipo = 'info', duracion = 4000) {
    const contenedor = obtenerContenedorToasts();
    const id = `toast-${++contadorToast}`;

    const iconos = {
        exito: '✓',
        ok: '✓',
        error: '✕',
        info: '▸',
        cargando: '⟳',
    };

    const toast = document.createElement('div');
    toast.id = id;
    toast.className = `toast toast--${tipo}`;

    const icono = document.createElement('span');
    icono.className = 'toast__icono';
    icono.textContent = iconos[tipo] ?? '▸';

    const textoEl = document.createElement('span');
    textoEl.className = 'toast__texto';
    textoEl.textContent = String(texto ?? '');

    const botonCerrar = document.createElement('button');
    botonCerrar.className = 'toast__cerrar';
    botonCerrar.type = 'button';
    botonCerrar.setAttribute('aria-label', 'Cerrar');
    botonCerrar.textContent = '✕';
    botonCerrar.addEventListener('click', () => cerrarToast(id));

    toast.append(icono, textoEl, botonCerrar);
    contenedor.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('toast--visible'));

    if (duracion > 0) {
        setTimeout(() => cerrarToast(id), duracion);
    }

    return id;
}

/**
 * Cierra y elimina un toast por su ID.
 * @param {string} id
 */
export function cerrarToast(id) {
    const toast = document.getElementById(id);
    if (!toast) return;
    toast.classList.remove('toast--visible');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
}

/**
 * Obtiene o crea el contenedor de toasts en el DOM.
 * @returns {HTMLElement}
 */
function obtenerContenedorToasts() {
    let contenedor = document.getElementById('contenedor-toasts');
    if (!contenedor) {
        contenedor = document.createElement('div');
        contenedor.id = 'contenedor-toasts';
        document.body.appendChild(contenedor);
    }
    return contenedor;
}

// ============================================================
//  Control de vistas (login ↔ dashboard)
// ============================================================

/**
 * Muestra una vista y oculta el resto.
 * @param {'vista-login'|'vista-dashboard'} idVista - ID del elemento a mostrar
 */
export function mostrarVista(idVista) {
    const vistas = document.querySelectorAll('.vista');
    vistas.forEach(v => v.classList.remove('vista--activa'));

    const vistaObjetivo = document.getElementById(idVista);
    if (vistaObjetivo) {
        vistaObjetivo.classList.add('vista--activa');
    } else {
        console.error(`[UI] Vista no encontrada: #${idVista}`);
    }
}

// ============================================================
//  Helpers del DOM
// ============================================================

/**
 * Habilita o deshabilita un botón con texto de carga opcional.
 * @param {string} idBoton
 * @param {boolean} habilitado
 * @param {string} [textoCarga] - Texto a mostrar cuando está deshabilitado
 */
export function toggleBoton(idBoton, habilitado, textoCarga = '') {
    const boton = document.getElementById(idBoton);
    if (!boton) return;
    boton.disabled = !habilitado;
    if (!habilitado && textoCarga) {
        boton.dataset.textoOriginal = boton.textContent;
        boton.textContent = textoCarga;
    } else if (habilitado && boton.dataset.textoOriginal) {
        boton.textContent = boton.dataset.textoOriginal;
        delete boton.dataset.textoOriginal;
    }
}

// ============================================================
//  Diálogo de confirmación (reemplaza window.confirm)
// ============================================================

/**
 * Muestra un modal de confirmación no-bloqueante.
 * Devuelve una Promise que resuelve true (confirmar) o false (cancelar).
 * @param {string} titulo - Pregunta principal
 * @param {string} [subtitulo] - Texto secundario opcional
 * @returns {Promise<boolean>}
 */
export function mostrarConfirmacion(titulo, subtitulo = '', opciones = {}) {
    return new Promise((resolver) => {
        const overlay     = document.getElementById('modal-confirmacion');
        const elTitulo    = document.getElementById('confirmacion-titulo');
        const elSubtitulo = document.getElementById('confirmacion-subtitulo');
        const elIcono     = overlay?.querySelector('.confirmacion-icono');
        const btnConfirmar = document.getElementById('btn-confirmar-si');
        const btnCancelar  = document.getElementById('btn-confirmar-no');

        if (!overlay || !elTitulo || !elSubtitulo || !btnConfirmar || !btnCancelar) {
            resolver(false);
            return;
        }

        // Capturar defaults antes de cualquier cambio, para restaurar al cerrar
        const iconoOriginal = elIcono?.textContent ?? '🗑️';
        const labelOriginal = btnConfirmar.textContent;

        elTitulo.textContent    = titulo;
        elSubtitulo.textContent = subtitulo;
        if (elIcono && opciones.icono !== undefined) elIcono.textContent = opciones.icono;
        overlay.classList.add('modal--visible');

        const nuevoConfirmar = btnConfirmar.cloneNode(true);
        const nuevoCancelar  = btnCancelar.cloneNode(true);
        btnConfirmar.replaceWith(nuevoConfirmar);
        btnCancelar.replaceWith(nuevoCancelar);

        if (opciones.labelConfirmar) {
            document.getElementById('btn-confirmar-si').textContent = opciones.labelConfirmar;
        }

        function cerrar(resultado) {
            overlay.classList.remove('modal--visible');
            if (elIcono) elIcono.textContent = iconoOriginal;
            if (opciones.labelConfirmar) {
                const btn = document.getElementById('btn-confirmar-si');
                if (btn) btn.textContent = labelOriginal;
            }
            resolver(resultado);
        }

        document.getElementById('btn-confirmar-si')?.addEventListener('click', () => cerrar(true), { once: true });
        document.getElementById('btn-confirmar-no')?.addEventListener('click', () => cerrar(false), { once: true });
    });
}